import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { getUserOrders, cleanupExpiredOrders } from '../../services/supabaseService';

export const Orders: React.FC = () => {
  const { user } = useAuth();
  const isLojista = user?.role_id === 6;
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await cleanupExpiredOrders();
      const data = await getUserOrders();
      setOrders(data || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const filteredOrders = orders.filter(o => {
    if (filter === 'all') return true;
    if (filter === 'pending') {
      return o.status === 'pending' || o.status === 'pending_payment' || o.status === 'waiting_payment';
    }
    return o.status === filter;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return { label: 'Pago', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };
      case 'pending':
      case 'pending_payment':
      case 'waiting_payment':
        return { label: 'Aguardando Pagamento', class: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
      case 'processing':
        return { label: 'Em Preparação', class: 'bg-blue-500/15 text-blue-400 border-blue-500/30' };
      case 'shipped':
        return { label: 'Enviado', class: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' };
      case 'delivered':
        return { label: 'Entregue', class: 'bg-teal-500/15 text-teal-400 border-teal-500/30' };
      case 'expired':
        return { label: 'Expirado', class: 'bg-slate-700/40 text-slate-400 border-slate-600/30' };
      case 'canceled':
      case 'cancelled':
        return { label: 'Cancelado', class: 'bg-rose-500/15 text-rose-400 border-rose-500/30' };
      default:
        return { label: status, class: 'bg-slate-800 text-slate-300 border-slate-700' };
    }
  };

  if (!user) {
    return (
      <div className="p-16 text-center text-slate-500 font-bold text-sm">
        Faça login para ver seu histórico de pedidos.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Lojista Banner shortcut if applicable */}
      {isLojista && (
        <div className="bg-purple-950/30 border border-purple-500/30 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center text-sm shrink-0">
              <i className="fas fa-store"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-white">Você é um lojista parceiro</p>
              <p className="text-[11px] text-slate-400">Gerencie as compras que clientes fizeram na sua loja</p>
            </div>
          </div>
          <Link
            to="/pedidos-recebidos"
            className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 self-start sm:self-auto shrink-0 shadow-md shadow-purple-950/40"
          >
            <span>Ver Pedidos Recebidos</span>
            <i className="fas fa-arrow-right text-[10px]"></i>
          </Link>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white italic tracking-tighter uppercase">
            Meus Pedidos
          </h1>
          <p className="text-xs text-slate-400 font-medium mt-0.5">
            Acompanhe o status e histórico de todas as suas compras
          </p>
        </div>

        {/* Status Filter */}
        <div className="relative">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full sm:w-44 bg-slate-900 border border-slate-800 text-slate-300 px-3.5 py-2 rounded-xl text-xs font-bold focus:outline-none focus:border-purple-500/60 appearance-none cursor-pointer pr-8"
          >
            <option value="all">Todos os Pedidos</option>
            <option value="pending">Aguardando Pagamento</option>
            <option value="paid">Pagos</option>
            <option value="shipped">Enviados</option>
            <option value="delivered">Entregues</option>
            <option value="expired">Expirados</option>
          </select>
          <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-slate-500 text-[10px]">
            <i className="fas fa-chevron-down"></i>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="py-20 text-center space-y-3">
          <div className="w-8 h-8 border-3 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Carregando seus pedidos...</p>
        </div>
      ) : filteredOrders.length > 0 ? (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const badge = getStatusBadge(order.status);
            const totalQty = order.order_items?.reduce((acc: number, item: any) => acc + (item.quantity || 1), 0) || 0;
            const refCode = order.external_reference
              ? order.external_reference.slice(-7).toUpperCase()
              : String(order.id).slice(-7).toUpperCase();

            return (
              <div
                key={order.id}
                className="bg-slate-900/60 border border-slate-800 hover:border-slate-700 rounded-xl p-4 sm:p-5 transition-all group"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Left: Store info & items */}
                  <div className="flex items-start sm:items-center gap-3.5 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-slate-800/80 rounded-xl flex items-center justify-center text-slate-400 group-hover:text-purple-400 group-hover:bg-purple-600/10 transition-colors shrink-0 border border-slate-700/60">
                      <i className="fas fa-box-open text-base"></i>
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Link
                          to={`/pedido/${order.id}`}
                          className="text-sm font-black text-white hover:text-purple-400 transition-colors uppercase tracking-tight"
                        >
                          #{refCode}
                        </Link>
                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${badge.class}`}>
                          {badge.label}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-slate-400">
                        <span className="text-slate-300 font-bold">
                          {order.store?.name || 'Loja Parceira'}
                        </span>
                        <span className="text-slate-600">•</span>
                        <span className="text-[11px] text-slate-500">
                          {new Date(order.created_at).toLocaleDateString('pt-BR')}
                        </span>
                        {order.payment_method?.name && (
                          <>
                            <span className="text-slate-600">•</span>
                            <span className="text-[11px] text-purple-400 font-semibold flex items-center gap-1">
                              <i className="fas fa-credit-card text-[10px]"></i>
                              {order.payment_method.name}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Items Preview */}
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400">
                        <span className="font-bold text-slate-300">
                          {totalQty} {totalQty === 1 ? 'item' : 'itens'}
                        </span>
                        {order.order_items && order.order_items.length > 0 && (
                          <span className="text-slate-500 truncate max-w-sm hidden sm:inline">
                            : {order.order_items.slice(0, 2).map((item: any) => item.product?.name).filter(Boolean).join(', ')}
                            {order.order_items.length > 2 ? ` +${order.order_items.length - 2}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Price & Link */}
                  <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-slate-800/80">
                    <div className="text-left sm:text-right">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Total</span>
                      <span className="text-base font-black text-white">
                        R$ {(Number(order.amount) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    <Link
                      to={`/pedido/${order.id}`}
                      className="px-3.5 py-2 bg-slate-800 hover:bg-purple-600 text-slate-300 hover:text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5"
                    >
                      <span>Ver Pedido</span>
                      <i className="fas fa-arrow-right text-[10px]"></i>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl p-12 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center text-slate-500 mx-auto">
            <i className="fas fa-shopping-bag text-xl"></i>
          </div>
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Nenhum pedido encontrado</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Você ainda não realizou compras através do cardume.
          </p>
          <Link
            to="/produtos"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-purple-950/40 mt-2"
          >
            <i className="fas fa-bag-shopping"></i>
            <span>Explorar Produtos</span>
          </Link>
        </div>
      )}
    </div>
  );
};
